/**
 * The Documents tab — the per-document actions menu (VALE-404).
 *
 * **One three-dot menu in the row's trailing slot, as every other record list in
 * the app does it.** `contact-row-actions-menu.tsx` is the established pattern:
 * a `ghost` / `icon-xs` trigger carrying `MoreHorizontal`, wrapped in a
 * `shrink-0` div that stops the click reaching the row.
 *
 * The action set is production's, unchanged and complete — View, Download,
 * certify/uncertify for every document, Replace and Uncategorise for a core
 * document only, Delete for an additional document only. A core document is
 * replaced rather than deleted out from under its slot, which is why the two
 * halves are exclusive.
 *
 * VALE-806 completes the round trip on the non-core branch: "Mark as Will /
 * Death Certificate / Grant of Representation" puts a misclassified document
 * INTO a slot, and "Move ID to …" re-files an identity document against the
 * right person. Both were one-way before — the menu could only ever un-file.
 *
 * A codicil is the one core document that is also deletable outright
 * (VALE-1015): it is core (grouped in the Core Documents section) but does not
 * fill one of the fixed slots the way a will/death-certificate/grant does, so
 * displacing it via Replace is not the only way out — it can go via Delete too,
 * exactly like an additional document.
 */

'use client';

import { Download, Eye, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  CORE_SLOT_TYPES,
  FileTypeSlug,
  isIdentitySlotType
} from '@/lib/services/files/file-types';
import { getCoreDocumentTypeLabel } from '@/components/documents/v2';
import type {
  CategoriseTargetType,
  DocumentItem,
  IdentityAssignee
} from '@/components/documents/v2';

export interface DocumentRowActionHandlers {
  /**
   * Show the document. This opens the in-page drawer rather than handing the
   * file to a browser tab — the drawer offers the tab as its own "Open" action
   * for anyone who wants the browser's viewer.
   */
  onOpen?: (doc: DocumentItem) => void;
  onDownload?: (documentId: number) => void;
  /*
    Returns a promise since VALE-1505, because the documents tab's handler now
    lets a failed update REJECT rather than swallowing it (the attribute row
    needs the rejection to show a refusal). This menu item is fire-and-forget,
    so it has to absorb what the row awaits, or a failed PATCH from here is an
    unhandled rejection. The toast still comes from the mutation's own onError.
  */
  onCertifiedCopyChange?: (
    documentId: number,
    isCertified: boolean
  ) => void | Promise<void>;
  /**
   * Save the user's own one-line note, or clear it with an empty string —
   * which reverts the row to the document analysis's sentence. Read by the
   * drawer's editor; the row itself only displays the resulting line.
   */
  onNotesChange?: (documentId: number, notes: string) => void | Promise<void>;
  /** Core only — reopens the slot's own typed upload dialog. */
  onReplace?: () => void;
  /** Core only — moves the document back into Additional documents. */
  onUncategorise?: (documentId: number) => void;
  /**
   * Non-core only — files the document into one of the matter's core slots
   * (VALE-806). The inverse of `onUncategorise`: without it, an AI misfile could
   * only ever be taken OUT of a slot, never corrected.
   */
  onCategorise?: (documentId: number, type: CategoriseTargetType) => void;
  /** The people an identity document can be filed against. Empty hides the submenu. */
  identityAssignees?: IdentityAssignee[];
  /** Identity documents only — moves the document onto a different person. */
  onAssignIdentity?: (documentId: number, assignee: IdentityAssignee) => void;
  /** Additional only. */
  onDelete?: (documentId: number) => void;
}

export function DocumentRowActions({
  doc,
  isCore,
  /**
   * Whether the menu leads with View and Download. The row needs them — they are
   * its only way to either. The drawer does not: it *is* the view, and carries
   * Download and Open as header buttons, so repeating them here would offer the
   * reader two controls for a thing they have already done.
   */
  showViewAndDownload = true,
  onOpen,
  onDownload,
  onCertifiedCopyChange,
  onReplace,
  onUncategorise,
  onCategorise,
  identityAssignees,
  onAssignIdentity,
  onDelete,
  onRegenerate
}: {
  doc: DocumentItem;
  isCore: boolean;
  showViewAndDownload?: boolean;
  /**
   * Regenerate a document Vale generated from one of the firm's precedents.
   * Omitted for every other document, which is nearly all of them.
   */
  onRegenerate?: () => void;
} & DocumentRowActionHandlers) {
  // An identity document lives in Additional documents (it fills a slot on a
  // CONTACT, not on the matter), so this branch sits alongside "Mark as …"
  // rather than replacing it.
  const canReassignIdentity =
    !!onAssignIdentity &&
    !!doc.typeSlug &&
    isIdentitySlotType(doc.typeSlug) &&
    (identityAssignees?.length ?? 0) > 0;

  // A codicil is core but not slot-filling (see the file banner above), so it
  // keeps Delete alongside Replace/Uncategorise rather than losing it the way
  // every other core document does.
  const isCodicil = doc.typeSlug === FileTypeSlug.CODICIL;

  return (
    <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
      {/* `modal={false}` because Delete and Replace open a Dialog in the same
          React commit that closes this menu: a modal dropdown writes
          `pointer-events: none` onto `<body>` and its restore races the
          dialog's own lock, leaving the page un-clickable until a refresh
          (LF-4072).

          Inside the document drawer's Sheet this request is IGNORED —
          `DropdownMenu` forces modality there, because a non-modal menu inside
          an overlay cannot be opened at all. That used to be this component's
          `insideSheet` prop, and getting it wrong is how the drawer's kebab
          went dead twice (VALE-1135, found again in VALE-934). It is now
          answered by where the menu renders rather than by what a call site
          passes; see `components/ui/page-overlay-scope.tsx`. The drawer answers
          the LF-4072 hazard by closing itself and firing Replace / Delete on
          the next frame, so the two modal layers never overlap in one commit. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="shrink-0"
            aria-label={`Actions for ${doc.name}`}
          >
            <MoreHorizontal className="text-muted-foreground h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          {/* First, because it is the only item here that acts on the document's
              *content* rather than on its filing — and because a lawyer looking
              for it is looking for this document's own history, not for Delete. */}
          {onRegenerate && (
            <>
              <DropdownMenuItem onClick={onRegenerate}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {showViewAndDownload && (
            <>
              <DropdownMenuItem onClick={() => onOpen?.(doc)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownload?.(doc.id)}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </DropdownMenuItem>

              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            onClick={() => {
              void Promise.resolve(
                onCertifiedCopyChange?.(doc.id, !doc.isCertifiedCopy)
              ).catch(() => {
                // Surfaced by useUpdateFile's onError toast; swallowed here so
                // the menu item does not raise an unhandled rejection.
              });
            }}
          >
            {doc.isCertifiedCopy
              ? 'Remove certification'
              : 'Mark as certified copy'}
          </DropdownMenuItem>

          {isCore && (
            <>
              {/* Only when there is somewhere for it to go (VALE-1135). It used
                  to render for every core row, including a codicil, whose
                  handler had no arm for it: a menu item that did nothing. */}
              {onReplace && (
                <DropdownMenuItem onClick={() => onReplace()}>
                  Replace
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onUncategorise?.(doc.id)}>
                Uncategorise
              </DropdownMenuItem>
            </>
          )}

          {!isCore && onCategorise && (
            <>
              {CORE_SLOT_TYPES.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => onCategorise(doc.id, type)}
                >
                  Mark as {getCoreDocumentTypeLabel(type)}
                </DropdownMenuItem>
              ))}
            </>
          )}

          {canReassignIdentity && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Move ID to</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {identityAssignees?.map((assignee) => (
                  <DropdownMenuItem
                    key={assignee.id}
                    onClick={() => onAssignIdentity?.(doc.id, assignee)}
                  >
                    {assignee.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {(!isCore || isCodicil) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-error-subtle-foreground focus:text-error-subtle-foreground"
                onClick={() => onDelete?.(doc.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
